import { Button, ButtonGroup, Input, LuIcon, toast } from "@overmap-ai/blocks"
import { useNavigate } from "@tanstack/react-router"
import type { Polygon } from "geojson"
import { LngLat } from "mapbox-gl"
import { type ChangeEventHandler, memo, useCallback, useState } from "react"
import { useMap } from "react-map-gl/mapbox"

import { PROJECT_NAME_FIELD_MAX_LENGTH } from "../../constants"
import { BOUNDS_PADDING } from "../../mapbox/constants"
import { pixelDistanceToDistance, polygonToLngLatBounds, square } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { BottomBar } from "../BottomBar/BottomBar"
import { LocationPickerOverlay } from "../Overlays/LocationPickerOverlay"
import { PolygonPickerOverlay } from "../Overlays/PolygonPicker"

enum SlideshowSlides {
	LOCATION,
	BOUNDS,
	NAME,
}

export type SlideshowResult = {
	projectName: string
	bounds: Polygon
}

interface SlideshowProps {
	onSubmitCallback: (result: SlideshowResult) => void
}

export const Slideshow = memo<SlideshowProps>(function Slideshow(props) {
	const { onSubmitCallback } = props

	const { current: map } = useMap()
	const [currentIndex, setCurrentIndex] = useState<SlideshowSlides>(SlideshowSlides.LOCATION)
	const [projectName, setProjectName] = useState<string>("")
	const [projectBounds, setProjectBounds] = useState<Polygon | undefined>()
	const navigate = useNavigate()

	const prevSlide = useCallback(() => {
		switch (currentIndex) {
			case SlideshowSlides.LOCATION:
				void navigate({
					from: "/$organizationId/settings/projects/create",
					to: "/$organizationId/settings/projects",
				})
				return
			case SlideshowSlides.BOUNDS:
				setCurrentIndex(SlideshowSlides.LOCATION)
				return
			case SlideshowSlides.NAME:
				setCurrentIndex(SlideshowSlides.BOUNDS)
				return
		}
	}, [navigate, currentIndex])

	const nextSlide = useCallback(() => {
		switch (currentIndex) {
			case SlideshowSlides.LOCATION:
				setCurrentIndex(SlideshowSlides.BOUNDS)
				return
			case SlideshowSlides.BOUNDS:
				setCurrentIndex(SlideshowSlides.NAME)
				return
			case SlideshowSlides.NAME:
				return
		}
	}, [currentIndex])

	const handleProjectNameChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
		setProjectName(e.target.value)
	}, [])

	const handleLocationPickerConfirm = useCallback(
		(position: LngLat) => {
			if (!map) return
			const size = pixelDistanceToDistance(map.getMap(), 400)
			setProjectBounds(square(position.toArray(), size).geometry)
			nextSlide()
		},
		[map, nextSlide],
	)

	const handlePolygonPickerConfirm = useCallback(
		(polygon: Polygon) => {
			if (!map) return
			setProjectBounds(polygon)
			map.fitBounds(polygonToLngLatBounds(polygon), { padding: BOUNDS_PADDING, animate: false })
			nextSlide()
		},
		[map, nextSlide],
	)

	const handleProjectNameConfirm = useCallback(() => {
		if (!projectName) {
			toast.error({ title: "Project name cannot be empty", description: "" })
			return
		}

		if (!projectBounds) {
			toast.error({ title: "Project bounds must be set", description: "" })
			return
		}

		onSubmitCallback({
			projectName,
			bounds: projectBounds,
		})
	}, [onSubmitCallback, projectBounds, projectName])

	return (
		<div className="pointer-events-none">
			{currentIndex === SlideshowSlides.LOCATION && (
				<LocationPickerOverlay onConfirm={handleLocationPickerConfirm} onCancel={prevSlide} />
			)}

			{currentIndex === SlideshowSlides.BOUNDS && (
				<PolygonPickerOverlay
					initialPolygon={projectBounds!}
					onConfirm={handlePolygonPickerConfirm}
					onCancel={prevSlide}
				/>
			)}

			{currentIndex === SlideshowSlides.NAME && (
				<>
					<BottomBar className="flex flex-col gap-2">
						<Input.Root accentColor="base" variant="outline">
							<Input.Field
								autoFocus
								value={projectName}
								onChange={handleProjectNameChange}
								type="text"
								placeholder="Enter a project name"
								maxLength={PROJECT_NAME_FIELD_MAX_LENGTH}
							/>
						</Input.Root>

						<ButtonGroup className="flex w-full gap-2" accentColor="base" size="sm" variant="soft">
							<Button onClick={prevSlide} className="shrink-0 grow basis-0">
								<LuIcon icon={modelIconMapping.close} />
								Cancel
							</Button>
							<Button
								onClick={handleProjectNameConfirm}
								accentColor="primary"
								variant="surface"
								className="shrink-0 grow basis-0"
							>
								<LuIcon icon={modelIconMapping.check} />
								Confirm
							</Button>
						</ButtonGroup>
					</BottomBar>
				</>
			)}
		</div>
	)
})

Slideshow.displayName = "Slideshow"
